import React, { useCallback, useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, Clock, HelpCircle, XCircle, FileText, ChevronDown } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const STATUS_META = {
  awaiting_counsellor_review: { label: "Awaiting review", tone: "bg-amber-50 text-amber-800 border-amber-200", Icon: Clock },
  approved:                   { label: "Approved",       tone: "bg-emerald-50 text-emerald-800 border-emerald-200", Icon: CheckCircle2 },
  info_requested:             { label: "Info requested", tone: "bg-sky-50 text-sky-800 border-sky-200", Icon: HelpCircle },
  declined:                   { label: "Declined",       tone: "bg-rose-50 text-rose-800 border-rose-200", Icon: XCircle },
};

export default function PatientReports() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/genome/my/reports");
      setReports(data.reports || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return null;
  if (reports.length === 0) return null;

  return (
    <div className="rounded-lg border border-border bg-white p-5" data-testid="genome-patient-reports">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Your uploads</div>
          <div className="mt-1 font-serif text-xl text-primary">Counsellor review status</div>
        </div>
        <div className="text-[11px] text-muted-foreground">{reports.length} report{reports.length > 1 ? "s" : ""}</div>
      </div>

      <ul className="mt-4 divide-y divide-border">
        {reports.map((r) => {
          const meta = STATUS_META[r.status] || { label: r.status, tone: "", Icon: FileText };
          const Icon = meta.Icon;
          const latest = r.latest_decision;
          const isOpen = !!open[r.id];
          return (
            <li key={r.id} className="py-3" data-testid={`patient-report-row-${r.id}`}>
              <button
                type="button"
                onClick={() => setOpen((p) => ({ ...p, [r.id]: !p[r.id] }))}
                className="w-full flex items-start gap-3 text-left"
              >
                <Icon size={16} className="mt-0.5 text-primary shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm text-primary truncate max-w-[16rem]">{r.filename}</span>
                    <Badge variant="outline" className={`text-[10px] ${meta.tone}`}>{meta.label}</Badge>
                  </div>
                  <div className="mt-1 text-[11px] text-muted-foreground">
                    Uploaded {new Date(r.created_at).toLocaleDateString()}
                    {latest ? ` · reviewed ${new Date(latest.at).toLocaleDateString()} by ${latest.reviewer_name || "Counsellor"}` : " · awaiting counsellor"}
                  </div>
                </div>
                <ChevronDown size={14} className={`mt-1 text-muted-foreground transition-transform ${isOpen ? "rotate-180" : ""}`} />
              </button>
              {isOpen && (
                <div className="mt-3 ml-7 rounded-md border border-dashed border-border p-3 bg-muted/30">
                  {latest ? (
                    <>
                      <div className="text-[11px] uppercase tracking-widest text-muted-foreground">
                        Counsellor note · {latest.action.replace("_", " ")}
                      </div>
                      <div className="mt-1 text-sm text-primary italic">"{latest.note}"</div>
                      {r.status === "approved" ? (
                        <div className="mt-2 text-[12px] text-emerald-800">
                          Cleared to act on the prevention map above.
                        </div>
                      ) : r.status === "info_requested" ? (
                        <div className="mt-2 text-[12px] text-sky-800">
                          Please upload the requested document from <b>/genome/start</b> (Genomics-first mode).
                        </div>
                      ) : r.status === "declined" ? (
                        <div className="mt-2 text-[12px] text-rose-800">
                          You can discuss this with your physician — your prevention map is still available.
                        </div>
                      ) : null}
                    </>
                  ) : (
                    <div className="text-sm text-muted-foreground">Your report is in the counsellor queue — most reviews happen within 48 hours.</div>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
